import crypto from 'crypto';

import type { TaskProviderLike, TaskLike } from '@roo-code/types';

import {
  type CloudUserInfo,
  type ExtensionInstance,
  type ExtensionBridgeControlChannelMessage,
  type ExtensionBridgeEventChannelMessage,
  type TaskBridgeControlChannelMessage,
  type TaskBridgeEventChannelMessage,
  TaskStatus,
  ConnectionState,
} from './types.js';
import { SocketConnectionManager } from './managers/SocketConnectionManager.js';
import { ExtensionManager } from './managers/ExtensionManager.js';
import { TaskManager } from './managers/TaskManager.js';

export interface UnifiedBridgeOptions {
  userId: string;
  socketBridgeUrl: string;
  token: string;
  provider: TaskProviderLike;
}

export class UnifiedBridgeService {
  private static instance: UnifiedBridgeService | null = null;

  // Core
  private readonly userId: string;
  private readonly socketBridgeUrl: string;
  private readonly token: string;
  private readonly provider: TaskProviderLike;
  private readonly instanceId: string;

  // Managers
  private connectionManager: SocketConnectionManager;
  private extensionManager: ExtensionManager | null = null;
  private taskManager: TaskManager | null = null;

  // Reconnection
  private readonly MAX_RECONNECT_ATTEMPTS = Infinity;
  private readonly RECONNECT_DELAY = 1_000;
  private readonly RECONNECT_DELAY_MAX = 30_000;

  public static getInstance(): UnifiedBridgeService | null {
    return UnifiedBridgeService.instance;
  }

  public static async createInstance(options: UnifiedBridgeOptions) {
    console.log('[UnifiedBridgeService] Creating new instance');
    UnifiedBridgeService.instance = new UnifiedBridgeService(options);
    await UnifiedBridgeService.instance.initialize();
    return UnifiedBridgeService.instance;
  }

  public static resetInstance() {
    if (UnifiedBridgeService.instance) {
      console.log('[UnifiedBridgeService] Resetting instance');
      UnifiedBridgeService.instance.disconnect().catch(() => {});
      UnifiedBridgeService.instance = null;
    }
  }

  private constructor(options: UnifiedBridgeOptions) {
    this.userId = options.userId;
    this.socketBridgeUrl = options.socketBridgeUrl;
    this.token = options.token;
    this.provider = options.provider;
    this.instanceId = crypto.randomUUID();

    console.log('[UnifiedBridgeService] Initialized with:', {
      userId: this.userId,
      socketBridgeUrl: this.socketBridgeUrl,
      instanceId: this.instanceId,
    });

    this.connectionManager = new SocketConnectionManager(
      'UnifiedBridgeService',
      {
        url: this.socketBridgeUrl,
        socketOptions: {
          query: {
            token: this.token,
            clientType: 'extension',
            instanceId: this.instanceId,
          },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: this.MAX_RECONNECT_ATTEMPTS,
          reconnectionDelay: this.RECONNECT_DELAY,
          reconnectionDelayMax: this.RECONNECT_DELAY_MAX,
        },
        onConnect: async () => {
          await this.handleConnect();
        },
        onDisconnect: () => {
          this.handleDisconnect();
        },
        onReconnect: async (attemptNumber) => {
          await this.handleReconnect(attemptNumber);
        },
      },
    );

    this.extensionManager = new ExtensionManager(
      this.instanceId,
      this.userId,
      this.provider,
      (message: ExtensionBridgeEventChannelMessage) =>
        this.publishExtensionEvent(message),
    );

    this.taskManager = new TaskManager(
      (message: TaskBridgeEventChannelMessage) =>
        this.publishTaskEvent(message),
    );
  }

  private async initialize() {
    await this.connectionManager.connect();
    this.setupSocketListeners();
  }

  private setupSocketListeners() {
    const socket = this.connectionManager.getSocket();

    if (!socket) {
      console.error('[UnifiedBridgeService] Socket not available');
      return;
    }

    // Remove any existing listeners first to prevent duplicates
    socket.off('extension:control:message');
    socket.off('task:control:message');
    socket.off('connected');

    socket.on(
      'extension:control:message',
      (message: ExtensionBridgeControlChannelMessage) => {
        console.log(
          '[UnifiedBridgeService] Received extension control message:',
          {
            type: message.type,
            instanceId: message.instanceId,
          },
        );

        this.extensionManager?.handleControlMessage(message);
      },
    );

    socket.on(
      'task:control:message',
      (message: TaskBridgeControlChannelMessage) => {
        console.log('[UnifiedBridgeService] Received task control message:', {
          type: message.type,
          taskId: message.taskId,
        });

        this.taskManager?.handleControlMessage(message);
      },
    );

    // socket.on(
    //   'task:event:message',
    //   (message: TaskBridgeEventChannelMessage) => {
    //     console.log('[UnifiedBridgeService] Received task event message:', {
    //       type: message.type,
    //       taskId: message.payload.taskId,
    //     });
    //   },
    // );

    socket.on('connected', (data) => {
      console.log('[UnifiedBridgeService] Connection confirmed:', data);
    });
  }

  private async handleConnect() {
    console.log('[UnifiedBridgeService] Connected');

    const socket = this.connectionManager.getSocket();

    if (!socket) {
      console.error(
        '[UnifiedBridgeService] Socket not available after connect',
      );

      return;
    }

    if (this.extensionManager) {
      await this.extensionManager.onConnect(socket);
    }

    if (this.taskManager) {
      await this.taskManager.onConnect(socket);
    }
  }

  private handleDisconnect() {
    console.log('[UnifiedBridgeService] Disconnected');

    if (this.extensionManager) {
      this.extensionManager.onDisconnect();
    }

    if (this.taskManager) {
      this.taskManager.onDisconnect();
    }
  }

  private async handleReconnect(attemptNumber: number) {
    console.log(
      '[UnifiedBridgeService] Reconnected after',
      attemptNumber,
      'attempts',
    );

    const socket = this.connectionManager.getSocket();

    if (!socket) {
      console.error(
        '[UnifiedBridgeService] Socket not available after reconnect',
      );

      return;
    }

    // Re-setup socket listeners to ensure they're properly configured
    // after automatic reconnection (Socket.IO's built-in reconnection)
    // The socket.off() calls in setupSocketListeners prevent duplicates
    this.setupSocketListeners();

    if (this.extensionManager) {
      await this.extensionManager.onReconnect(socket);
    }

    if (this.taskManager) {
      await this.taskManager.onReconnect(socket);
    }
  }

  // Extension API

  public getExtensionInstance(): ExtensionInstance | null {
    return this.extensionManager?.getExtensionInstance() ?? null;
  }

  public updateExtensionTask(taskId: string, status: TaskStatus): void {
    this.extensionManager?.updateTask(taskId, status);
  }

  // Task API

  public async subscribeToTask(task: TaskLike): Promise<void> {
    if (!this.taskManager) {
      throw new Error('Task management is not enabled');
    }

    const socket = this.connectionManager.getSocket();

    if (!socket || !this.connectionManager.isConnected()) {
      console.warn(
        '[UnifiedBridgeService] Cannot subscribe to task: not connected. Will retry when connected.',
      );

      this.taskManager.addPendingTask(task);

      const state = this.connectionManager.getConnectionState();

      if (
        state === ConnectionState.DISCONNECTED ||
        state === ConnectionState.FAILED
      ) {
        this.initialize();
      }

      return;
    }

    await this.taskManager.subscribeToTask(task, socket);
  }

  public async unsubscribeFromTask(taskId: string): Promise<void> {
    if (!this.taskManager) {
      throw new Error('Task management is not enabled');
    }

    const socket = this.connectionManager.getSocket();
    if (!socket) {
      return;
    }

    await this.taskManager.unsubscribeFromTask(taskId, socket);
  }

  public getSubscribedTasks(): Map<string, TaskLike> {
    return this.taskManager?.getSubscribedTasks() ?? new Map();
  }

  // Publish

  private async publishExtensionEvent(
    message: ExtensionBridgeEventChannelMessage,
  ): Promise<boolean> {
    const socket = this.connectionManager.getSocket();

    if (!socket || !this.connectionManager.isConnected()) {
      console.error(
        '[UnifiedBridgeService] Cannot publish extension event: not connected',
      );

      return false;
    }

    try {
      socket.emit('extension:event', message);

      console.log(
        '[UnifiedBridgeService] Published extension event:',
        message.type,
      );

      return true;
    } catch (error) {
      console.error(
        '[UnifiedBridgeService] Failed to publish extension event:',
        error,
      );

      return false;
    }
  }

  private async publishTaskEvent(
    message: TaskBridgeEventChannelMessage,
  ): Promise<boolean> {
    const socket = this.connectionManager.getSocket();
    if (!socket || !this.connectionManager.isConnected()) {
      console.error(
        '[UnifiedBridgeService] Cannot publish task event: not connected',
      );

      return false;
    }

    try {
      socket.emit('task:event', message);
      console.log('[UnifiedBridgeService] Published task event:', message.type);
      return true;
    } catch (error) {
      console.error(
        '[UnifiedBridgeService] Failed to publish task event:',
        error,
      );

      return false;
    }
  }

  // Shared API

  public getConnectionState(): ConnectionState {
    return this.connectionManager.getConnectionState();
  }

  public async disconnect(): Promise<void> {
    console.log('[UnifiedBridgeService] Disconnecting...');

    if (this.extensionManager) {
      await this.extensionManager.cleanup(this.connectionManager.getSocket());
    }

    if (this.taskManager) {
      await this.taskManager.cleanup(this.connectionManager.getSocket());
    }

    await this.connectionManager.disconnect();
    UnifiedBridgeService.instance = null;

    console.log('[UnifiedBridgeService] Disconnected');
  }

  public async reconnect(): Promise<void> {
    await this.connectionManager.reconnect();
    // After a manual reconnect, we have a new socket instance
    // so we need to set up listeners again
    this.setupSocketListeners();
  }

  public get connected(): boolean {
    return this.connectionManager.isConnected();
  }

  public get subscribedTaskCount(): number {
    return this.taskManager?.getSubscribedTasks().size ?? 0;
  }

  public static async handleRemoteControlState(
    userInfo: CloudUserInfo | null,
    remoteControlEnabled: boolean | undefined,
    options: UnifiedBridgeOptions,
    logger?: (message: string) => void,
  ) {
    if (userInfo?.extensionBridgeEnabled && remoteControlEnabled) {
      const existingService = UnifiedBridgeService.getInstance();

      if (!existingService) {
        try {
          const service = await UnifiedBridgeService.createInstance(options);
          const state = service.getConnectionState();

          logger?.(
            `[UnifiedBridgeService#handleRemoteControlState] Instance created (state: ${state})`,
          );

          if (state !== ConnectionState.CONNECTED) {
            logger?.(
              `[UnifiedBridgeService#handleRemoteControlState] Service is not connected yet, will retry in background`,
            );
          }
        } catch (error) {
          const message = `[UnifiedBridgeService#handleRemoteControlState] Failed to create instance: ${
            error instanceof Error ? error.message : String(error)
          }`;

          logger?.(message);
          console.error(message);
        }
      } else {
        const state = existingService.getConnectionState();
        if (
          state === ConnectionState.FAILED ||
          state === ConnectionState.DISCONNECTED
        ) {
          logger?.(
            `[UnifiedBridgeService#handleRemoteControlState] Existing service is ${state}, attempting reconnection`,
          );
          existingService.reconnect().catch((error) => {
            const message = `[UnifiedBridgeService#handleRemoteControlState] Reconnection failed: ${
              error instanceof Error ? error.message : String(error)
            }`;
            logger?.(message);
            console.error(message);
          });
        }
      }
    } else {
      const existingService = UnifiedBridgeService.getInstance();

      if (existingService) {
        try {
          await existingService.disconnect();
          UnifiedBridgeService.resetInstance();

          logger?.(
            `[UnifiedBridgeService#handleRemoteControlState] Service disconnected and reset`,
          );
        } catch (error) {
          const message = `[UnifiedBridgeService#handleRemoteControlState] Failed to disconnect and reset instance: ${
            error instanceof Error ? error.message : String(error)
          }`;
          logger?.(message);
          console.error(message);
        }
      }
    }
  }
}
